import * as React from 'react';
import { Link } from 'react-router';

/** Single source of truth for the waitlist deadline — update this one line to move the date. */
export const WAITLIST_CLOSES_AT = '2026-09-04T00:00:00Z';

/**
 * Single source of truth for the CLOSED BETA deadline. The announcement post, its countdown and
 * the site-wide banner all read this line, so the date is stated once and cannot disagree with
 * itself. The gates close at the end of the day on 30 September 2026.
 */
export const BETA_CLOSES_AT = '2026-09-30T23:59:59Z';

/**
 * The gate that is open RIGHT NOW. This is the one place to move when an announcement is
 * superseded (alpha -> beta -> ...): the sticky banner, the landing-page timer and the
 * announcement post all read it, so they cannot disagree about the gate or the date.
 */
export const LIVE_GATE = {
  postPath: '/blog/the-raisy-beta-is-open',
  target: BETA_CLOSES_AT,
  /** Sticky banner, small type. */
  label: 'Beta closes in',
  /** The big timer's headline, on the post and on the landing page. */
  kicker: 'Beta Closes',
  closedMessage: 'The beta has closed.',
  cta: 'Join now →',
} as const;

function getRemaining(target: string) {
  const diff = new Date(target).getTime() - Date.now();
  const clamped = Math.max(diff, 0);
  const totalSeconds = Math.floor(clamped / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: diff <= 0,
  };
}

const Unit = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center min-w-[64px] sm:min-w-[84px]">
    <span className="text-4xl sm:text-6xl font-bold tabular-nums text-white leading-none drop-shadow-[0_0_12px_rgba(220,38,38,0.6)]">
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-2 text-label-xs sm:text-xs font-semibold uppercase tracking-widest text-red-300/80">
      {label}
    </span>
  </div>
);

/**
 * Big countdown to a gate's deadline. Recalculates from the wall clock every
 * second — never a stored/derived duration — so it stays correct across
 * reloads, timezones, and however long the tab stays open.
 *
 * `kicker` / `closedMessage` name the gate the timer belongs to. They default to the original
 * waitlist wording, so an announcement post that passes only a `target` (the closed beta) reads
 * "Beta Closes" rather than inheriting the alpha's label.
 *
 * `actionHref` / `actionLabel` put the way in INSIDE the timer (a host page then needs one
 * component call, not a timer plus a separate button). The action renders only while the gate is
 * open — a button saying "join" under "the beta has closed" would be a lie.
 */
export const CountdownTimer = ({
  target = WAITLIST_CLOSES_AT,
  kicker = 'Waitlist Closes',
  closedMessage = 'The waitlist has closed.',
  actionHref,
  actionLabel,
}: {
  target?: string;
  kicker?: string;
  closedMessage?: string;
  actionHref?: string;
  actionLabel?: string;
}) => {
  const [remaining, setRemaining] = React.useState(() => getRemaining(target));

  React.useEffect(() => {
    const id = window.setInterval(() => setRemaining(getRemaining(target)), 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const deadlineLabel = new Date(target).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  if (remaining.expired) {
    return (
      <div className="not-prose rounded-xl border border-red-800 bg-red-950/40 px-6 py-5 text-center">
        <p className="text-red-300 font-semibold">{closedMessage}</p>
      </div>
    );
  }

  return (
    <div className="not-prose rounded-xl border-2 border-red-600 bg-gradient-to-b from-slate-950 to-red-950/50 px-6 py-8 text-center shadow-[0_0_40px_-10px_rgba(220,38,38,0.5)]">
      <p className="text-sm sm:text-base font-extrabold uppercase tracking-widest text-red-500 mb-5 animate-pulse">
        {kicker}
      </p>
      <div className="flex items-start justify-center gap-3 sm:gap-6">
        <Unit value={remaining.days} label="Days" />
        <span className="text-3xl sm:text-5xl font-bold text-red-700 leading-none pt-1 sm:pt-2">:</span>
        <Unit value={remaining.hours} label="Hours" />
        <span className="text-3xl sm:text-5xl font-bold text-red-700 leading-none pt-1 sm:pt-2">:</span>
        <Unit value={remaining.minutes} label="Min" />
        <span className="text-3xl sm:text-5xl font-bold text-red-700 leading-none pt-1 sm:pt-2">:</span>
        <Unit value={remaining.seconds} label="Sec" />
      </div>
      <p className="mt-5 text-sm font-semibold text-red-300">Once it closes, this door does not reopen for the next round.</p>
      <p className="mt-1 text-xs text-slate-400">{deadlineLabel}</p>
      {actionHref && actionLabel && (
        <p className="mt-6">
          <Link
            to={actionHref}
            className="inline-block rounded-lg bg-red-600 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-500"
          >
            {actionLabel}
          </Link>
        </p>
      )}
    </div>
  );
};
